import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import { carouselSlides } from "@/content/landing";

const AUTO_MS = 5000;

export default function ProductCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const count = carouselSlides.length;

  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const go = useCallback((next) => setIndex((i) => (next + count) % count), [count]);

  useEffect(() => {
    if (paused || prefersReduced || count <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), AUTO_MS);
    return () => clearInterval(t);
  }, [paused, prefersReduced, count]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) go(index + (dx < 0 ? 1 : -1));
    touchStartX.current = null;
  };

  const [brokenIds, setBrokenIds] = useState({});
  const slide = carouselSlides[index];
  const showImg = slide.src && !brokenIds[slide.id];

  return (
    <section className="py-20 px-6 bg-white">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
            Veja o sistema funcionando
          </h2>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          role="region"
          aria-roledescription="carrossel"
          aria-label="Capturas de tela do Tuto"
        >
          {/* Device frame */}
          <div className="bg-[#F4F7FB] border border-[#D1DEEE] rounded-lg p-3">
            <div className="bg-white rounded-xl overflow-hidden border border-[#D1DEEE]">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[#D1DEEE] bg-[#F4F7FB]">
                <span className="w-3 h-3 rounded-full bg-[#D1DEEE]" />
                <span className="w-3 h-3 rounded-full bg-[#D1DEEE]" />
                <span className="w-3 h-3 rounded-full bg-[#D1DEEE]" />
              </div>
              <div className="aspect-[16/10] flex items-center justify-center bg-[#F8FAFD]">
                {showImg ? (
                  <img
                    src={slide.src}
                    alt={slide.alt}
                    width={1280}
                    height={800}
                    loading="lazy"
                    onError={() => setBrokenIds((b) => ({ ...b, [slide.id]: true }))}
                    className="w-full h-full object-contain bg-white"
                  />
                ) : (
                  // Placeholder até os screenshots reais (// TODO assets)
                  <div className="flex flex-col items-center gap-3 text-[#9bb0cc]">
                    <ImageIcon size={48} strokeWidth={1.2} />
                    <span className="text-sm">{slide.alt}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Caption */}
          <p className="text-center text-[#335480] text-base mt-5 max-w-2xl mx-auto min-h-[2.5rem]">
            {slide.caption}
          </p>

          {/* Arrows */}
          <button
            onClick={() => go(index - 1)}
            aria-label="Slide anterior"
            className="absolute left-0 top-1/3 -translate-y-1/2 -ml-2 sm:-ml-5 w-10 h-10 rounded-full bg-white border border-[#D1DEEE] shadow flex items-center justify-center text-[#335480] hover:text-[#003882]"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => go(index + 1)}
            aria-label="Próximo slide"
            className="absolute right-0 top-1/3 -translate-y-1/2 -mr-2 sm:-mr-5 w-10 h-10 rounded-full bg-white border border-[#D1DEEE] shadow flex items-center justify-center text-[#335480] hover:text-[#003882]"
          >
            <ChevronRight size={20} />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {carouselSlides.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setIndex(i)}
              aria-label={`Ir para o slide ${i + 1}: ${s.caption}`}
              aria-current={i === index}
              className={`h-2.5 rounded-full transition-all ${i === index ? "w-6 bg-[#003882]" : "w-2.5 bg-[#D1DEEE] hover:bg-[#9bb0cc]"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
